<template>
    <button
        :class="[
            $style.button,
            $style[`size_${props.size}`],
            props.disabled && $style.disabled,
            isSelected && $style.selected,
        ]"
        :disabled="props.disabled"
        data-interface-target="switch-locale"
        @click="updateLocale"
    >
        <Icon
            :name="locale"
            :alt="`switch_to_${localeName}`"
            :class="$style.icon"
        />
        <Typography
            type="label"
            size="xs"
            :responsive="false"
        >
            {{ localeName }}
        </Typography>
    </button>
</template>

<script lang="ts" setup>
import Icon from '@/components_new/Icon/Icon.vue'
import Typography from '@/components_new/Typography.vue'
import { AvailableLocale } from '@/i18n/types'
import { computed } from 'vue'
import { updateLocaleCookie } from '@/utils/cookie-parser'
import { useI18nAddon } from '@/i18n/i18n'
import { useRouter } from 'vue-router'
import { LOCALE_TITLES } from '@/i18n/helpers'

const i18nAddon = useI18nAddon()
const router = useRouter()

interface LocaleSwitcherProps {
    disabled?: boolean
    selected?: boolean
    size?: 'm' | 'l'
    locale: AvailableLocale
}

const props = defineProps<LocaleSwitcherProps>()

const localeName = computed(() => LOCALE_TITLES[props.locale])

const { currentLocale } = useI18nAddon()
const isSelected = computed(() => props.locale === currentLocale.value)

function updateLocale() {
    if (props.locale === currentLocale.value) {
        return
    }
    i18nAddon.setPreferredLocale(props.locale)
    updateLocaleCookie(props.locale)

    const hreflang = document
        .querySelector(`head link[data-locale="${props.locale}"]`)
        ?.getAttribute('data-lang-switch')
    if (!hreflang) {
        return
    }
    router.replace(hreflang)
}
</script>

<style module>
/* Base button style */
.button {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 1000px;
    padding: 6px 12px 6px 6px;
    --text: var(--white-60);
    --main: var(--black);
    --border-color: var(--carbon-400);
    color: var(--text);
    background-color: var(--main);
    border: 1px solid var(--border-color);
}

.disabled {
    cursor: not-allowed;
}
.selected {
    cursor: default;
}

.button:not(.disabled):hover {
    --text: var(--white-80);
    --border-color: var(--carbon-500);
}

.button:not(.disabled):active,
.button:not(.disabled).selected {
    --text: var(--white);
    --border-color: var(--carbon-400);
    --main: var(--carbon-400);
}

.button:focus-visible {
    outline: none;
    --border-color: var(--carbon-500);
}

.button.disabled {
    --text: var(--white-40);
    --background-color: var(--carbon-300);
    --main: var(--carbon-300);
}

/* Sizes */

.button.size_m {
    gap: 6px;
}

.size_m .icon {
    width: 24px;
    height: 24px;
}

.button.size_l {
    gap: 8px;
}
.size_l .icon {
    width: 32px;
    height: 32px;
}
</style>
